import * as React from 'react';
import AssignmentLateOutlined from '@material-ui/icons/AssignmentLateOutlined';
import moment from 'moment';
import { CountryIso2 } from '../../../../helpers/enums';
import { fetchVerificationsIfNeeded } from 'actions/verifications';
import { IVerification, VerificationNames } from 'interfaces/verification';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { PendingOutlinedIcon } from '../../PendingOutlinedIcon';
import { ToDoChild, ToDoDescriptor } from '../types';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

export const useVerificationsToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { verifications, isFetching, error } = useTypedSelector(state => state.verifications);
  const {
    mainSubscription: { countryISO2 }
  } = useTypedSelector(state => state.mainSubscription);

  React.useEffect(() => {
    const shouldFetch = ![CountryIso2.GB, CountryIso2.AU].includes(countryISO2 as CountryIso2);

    if (organizationId && shouldFetch) {
      dispatch(fetchVerificationsIfNeeded(organizationId));
    }
  }, [organizationId, dispatch, countryISO2]);

  const hasError = !!error;

  const reviewedVerifications =
    verifications
      ?.filter(({ name }) => !name.includes(VerificationNames.OshaCitation))
      .filter(({ isPendingReview }) => !isPendingReview) || [];

  const expired = reviewedVerifications.filter(
    ({ expirationDate }) => expirationDate !== null && moment(expirationDate).isBefore()
  );
  const expiring = reviewedVerifications.filter(
    ({ expirationDate, expiringPeriodDays }) =>
      expirationDate !== null && moment(expirationDate).isBetween(moment(), moment().add(expiringPeriodDays, 'days'))
  );

  const toChildren = (list: IVerification[], isExpired: boolean): ToDoChild[] =>
    list.map((verification, index) => {
      const expirationDate =
        verification.expirationDate && localizeDate({ value: verification.expirationDate, timeZoneId: 'UTC' }, t);
      return {
        id: `${verification.name}-${index}`,
        href: `/organizations/${organizationId}/questionnaire-v2/${verification.questionSectionId}`,
        label: t(
          isExpired ? 'newDashboard.toDoWidget.expiredOn' : 'newDashboard.toDoWidget.expiresOn',
          isExpired ? '{{name}} expired on {{date}}' : '{{name}} expires on {{date}}',
          {
            name: verification.name,
            date: expirationDate
          }
        )
      };
    });

  return [
    {
      id: 'verifications.expired',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiredVerifications', 'Update expired verifications'),
      icon: <AssignmentLateOutlined />,
      count: expired.length,
      children: toChildren(expired, true),
      isVisible: expired.length > 0 && !hasError,
      isFetching
    },
    {
      id: 'verifications.expiring',
      variant: 'group',
      label: t('newDashboard.toDoWidget.updateExpiringVerifications', 'Update verifications - expiring soon'),
      icon: <PendingOutlinedIcon />,
      count: expiring.length,
      children: toChildren(expiring, false),
      isVisible: expiring.length > 0 && !hasError,
      isFetching
    }
  ];
};
